'use strict';
const Ajv = require('ajv');
const angular = require('angular');
const {copy, isDate, isFunction, isNumber, isObject} = angular;

FormUtils.$inject = [
  'FirebaseUtil',
  '$domainValidation',
];

const OPTIONS_CONSTRAINTS = {
  text_short: {
    required: {type: 'boolean', advancedOption: true, defaultValue: false},
    isNumber: {type: 'boolean', advancedOption: true, defaultValue: false},
  },
  text_long: {
    required: {type: 'boolean', advancedOption: true, defaultValue: false},
    minLength: {type: 'number', min: 0, advancedOption: true, defaultValue: 0},
    maxLength: {type: 'number', min: 0, advancedOption: true, defaultValue: 0},
  },
  date: {
    required: {type: 'boolean', advancedOption: true, defaultValue: false},
    includeDate: {type: 'boolean', advancedOption: true, defaultValue: true},
    includeTime: {type: 'boolean', advancedOption: true, defaultValue: false},
  },
  duration: {
    required: {type: 'boolean', advancedOption: true, defaultValue: false},
    defaultUnit: {
      type: 'string',
      inList: ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds'],
      advancedOption: true,
      defaultValue: 'hours',
    },
  },
  scale: {
    required: {type: 'boolean', advancedOption: true, defaultValue: false},
    startNumber: {type: 'number', advancedOption: true, defaultValue: 0},
    endNumber: {type: 'number', advancedOption: true, defaultValue: 100},
    step: {type: 'number', min: 1, advancedOption: true, defaultValue: 1},
    range: {type: 'boolean', advancedOption: true, defaultValue: false},
  },
  list: {
    required: {type: 'boolean', advancedOption: true, defaultValue: false},
    items: {type: 'JSON', defaultValue: []}, // TODO: needs custom validator to confirm array of non-empty strings
    next: {type: 'number', min: 0, defaultValue: 0},
    selectMany: {type: 'boolean', advancedOption: true, defaultValue: false},
  },
  location: {
    required: {type: 'boolean', advancedOption: true, defaultValue: false},
    captureUserLocation: {type: 'boolean', advancedOption: true, defaultValue: false},
  },
  file: {
    required: {type: 'boolean', advancedOption: true, defaultValue: false},
    expectedFileCount: {type: 'number', min: 0, advancedOption: true, defaultValue: 0},
    expectedFileCountApproximation: {type: 'boolean', advancedOption: true, defaultValue: false},
  },
  reference_link: {
    required: {type: 'boolean', advancedOption: true, defaultValue: false},
    domainCollections: {type: 'JSON', defaultValue: []}, // TODO: needs custom validator to confirm array with valid contents
    selectMany: {type: 'boolean', advancedOption: true, defaultValue: false},
  },
};

/**
 * @enum FieldType
 */
const FieldType = {
  text_short: 'text_short',
  text_long: 'text_long',
  date: 'date',
  duration: 'duration',
  scale: 'scale',
  list: 'list',
  location: 'location',
  file: 'file',
  reference_link: 'reference_link',
};

// TODO: add "legacy" property as an optional boolean type
/* eslint-disable comma-dangle */
const FieldSchema = {
  "properties": {
    "id": {
      "title": "id",
      "description": "unique identifier for this field for reference across multiple versions of the same form",
      "type": "string"
    },
    "title": {
      "title": "title",
      "type": "string",
      "minLength": 1,
      "maxLength": 200
    },
    "description": {
      "title": "description",
      "type": "string",
      "minLength": 0,
      "maxLength": 1000
    },
    "type": {
      "title": "type",
      "description": "",
      "type": "string",
      "enum": ["text_short", "text_long", "date", "duration", "scale", "list", "location", "file", "reference_link"]
    },
    "version": {
      "title": "version",
      "description": "hash of this field. unedited fields across multiple form versions will share the same hash",
      "type": "string"
    },
// TODO: match with oneOf. will probably need a custom validator to apply the correct schema based on selected type
    "options": {
      "title": "options",
      "description": "value constraints vary by field type",
      "type": "object",
      "anyOf": [
        {
          "meta": {"forType": "text_short"},
          "properties": {
            "required": {"type": "boolean", "default": false},
            "isNumber": {"type": "boolean", "default": false}
          }
        }, {
          "meta": {"forType": "text_long"},
          "properties": {
            "required": {"type": "boolean", "default": false},
            "minLength": {"type": "number", "minimum": 0, "default": 0},
            "maxLength": {"type": "number", "minimum": 0, "default": 0}
          }
        }, {
          "meta": {"forType": "date"},
          "properties": {
            "required": {"type": "boolean", "default": false},
            "includeDate": {"type": "boolean", "default": true},
            "includeTime": {"type": "boolean", "default": false}
          }
        }, {
          "meta": {"forType": "duration"},
          "properties": {
            "required": {"type": "boolean", "default": false},
            "defaultUnit": {
              "type": "string",
              "enum": ["years", "months", "weeks", "days", "hours", "minutes", "seconds"],
              "default": "hours"
            }
          }
        }, {
          "meta": {"forType": "scale"},
          "properties": {
            "required": {"type": "boolean", "default": false},
            "startNumber": {"type": "number", "default": 0},
            "endNumber": {"type": "number", "default": 100},
            "step": {"type": "number", "minimum": 1, "default": 1},
            "range": {"type": "boolean", "default": false}
          }
        }, {
          "meta": {"forType": "list"},
          "properties": {
            "required": {"type": "boolean", "default": false},
            "items": {
              "type": "array",
              "items": {
                "type": "object",
                "properties": {
                  "id": {"type": "number"},
                  "value": {"type": "string"}
                }
              },
              "default": []
            },
            "next": {"type": "number", "minimum": 0, "default": 0},
            "selectMany": {"type": "boolean", "default": false}
          }
        }, {
          "meta": {"forType": "location"},
          "properties": {
            "required": {"type": "boolean", "default": false},
            "captureUserLocation": {"type": "boolean", "default": false}
          }
        }, {
          "meta": {"forType": "file"},
          "properties": {
            "required": {"type": "boolean", "default": false},
            "expectedFileCount": {"type": "number", "minimum": 0, "default": 0},
            "expectedFileCountApproximation": {"type": "boolean", "default": false}
          }
        }, {
          "meta": {"forType": "reference_link"},
          "properties": {
            "required": {"type": "boolean", "default": false},
            "domainCollections": {"type": "array", "items": {"type": "string"}, "default": []},
            "selectMany": {"type": "boolean", "default": false}
          }
        }
      ]
    }
  }
};
/* eslint-enable comma-dangle */

/** @namespace */
export function FormUtils(FirebaseUtil, $domainValidation) {

  const avj = new Ajv({allErrors: true});
  const fieldValidator = avj.compile(FieldSchema);

  this.generateOptionsConstraints = generateOptionsConstraints;
  this.generateOptions = generateOptions;
  this.serialize = serialize;
  this.serializeResponseFields = serializeResponseFields;
  this.serializeDates = serializeDates;
  this.hydrate = hydrate;
  this.hydrateResponseFields = hydrateResponseFields;
  this.hydrateDates = hydrateDates;
  this.generateField = generateField;
  this.validateField = validateField;
  this.validateOptions = validateOptions;

  // DO NOT MUTATE. TODO: consider freezing with immutable library
  /** @memberof FormUtils */
  this.OPTIONS_CONSTRAINTS = OPTIONS_CONSTRAINTS;

  // TODO: need better immutability strategy
  /** @memberof FormUtils.prototype */
  function generateOptionsConstraints(type) {
    return type ? Object.create(OPTIONS_CONSTRAINTS[type]) : Object.create(OPTIONS_CONSTRAINTS);
  }

  /** @memberof FormUtils.prototype */
  function serialize(value) {
    return value !== null ? JSON.stringify(value) : null;
  }

  /** @memberof FormUtils.prototype */
  function hydrate(value) {
    if (value === null) {
      return null;
    } else {
      const fields = JSON.parse(value);
      for (let i = 0, length = fields.length; i < length; i++) {
        if (fields[i].type === 'date' && isObject(fields[i].result) && isNumber(fields[i].result.value))
          fields[i].result.value = new Date(fields[i].result.value);
      }
      return fields;
    }
  }

  /** @memberof FormUtils.prototype */
  function serializeResponseFields(fields) {
    return fields !== null ? JSON.stringify(serializeDates(fields)) : null;
  }

  /** @memberof FormUtils.prototype */
  function serializeDates(fields) {
    if (!fields) {
      return null;
    } else {
      const length = fields.length;
      const output = new Array(length);
      let clone;
      for (let i = 0; i < length; i++) {
        if (fields[i].type === 'date' && isDate(fields[i].result.value)) {
          clone = copy(fields[i]);
          clone.result.value = fields[i].result.value.getTime();

          output[i] = clone;
        } else {
          output[i] = fields[i];
        }
      }

      return output;
    }
  }

  /** @memberof FormUtils.prototype */
  function hydrateResponseFields(fields) {
    return fields !== null ? hydrateDates(JSON.parse(fields), true) : null;
  }

  /** @memberof FormUtils.prototype */
  function hydrateDates(fields, isCloned) {
    if (!fields) {
      return null;
    } else {
      const output = isCloned ? fields : copy(fields);
      for (let i = 0, length = fields.length; i < length; i++) {
        if (fields[i].type === 'date' && isNumber(fields[i].result.value))
          fields[i].result.value = new Date(fields[i].result.value);
      }

      return output;
    }
  }

  /**
   * @memberof FormUtils
   * @param {FieldType} [type=FieldType.text_short]
   */
  function generateField(type) {
    if (!type) type = FieldType.text_short;

    return {
      id: FirebaseUtil.generateKey(),
      title: '',
      description: '',
      type: type,
      options: generateOptions(type),
    };
  }

  /** @memberof FormUtils.prototype */
  function generateOptions(type) {
    const constraints = OPTIONS_CONSTRAINTS[type];
    const result = {};

    for (const constraint in constraints) {
      if (constraints.hasOwnProperty(constraint)) {
        result[constraint] = constraints[constraint].defaultValue;

        if (isObject(result[constraint])) {
          result[constraint] = copy(result[constraint]);
        }
      }
    }

    return result;
  }

  /** @memberof FormUtils.prototype */
  function validateField(field) {
    const isValid = fieldValidator(field);
    const errors = fieldValidator.errors;
    fieldValidator.errors = null;
    return isValid ? isValid : errors;
  }

  // TODO: review this in greater depth once it can be called correctly
  /** @memberof FormUtils.prototype */
  function validateOptions(options, instance) {
    if (instance && (!options || !instance.type)) return 'FormFieldOptions.validation.null.message';

    if (!isObject(options) || isFunction(options)) return 'typeMismatch.JSON';

    let errors;
    const constraints = OPTIONS_CONSTRAINTS[instance.type];

    for (const constraint in constraints) {
      if (constraints.hasOwnProperty(constraint)) {
        errors = $domainValidation.validateConstraint(
          options[constraint],
          constraints[constraint],
          instance.$errors,
          'FormFieldOptions',
          constraint,
          instance,
        );
        if (errors.length > 0) {
          return errors;
        }
      }
    }

    return true;
  }
}



// WEBPACK FOOTER //
// ./src/js/forms/services/FormUtils.js